export type PackageVolumeUnit = 'm3' | 'cm3'
export type PackageWeightUnit = 'kg' | 'g'
export type PackageChargeWeightUnit = 'kgs' | 'gs'
export type PackageLengthUnit = 'mm' | 'cm' | 'm'
export type PackageMetricEditableField =
  | 'totalQty'
  | 'totalBoxQty'
  | 'totalPalletQty'
  | 'totalVolume'
  | 'totalWeight'
  | 'chargeWeight'
  | 'length'
  | 'width'
  | 'height'

export interface PackageUnitOption<T extends string> {
  label: string
  value: T
}

export interface PackageMetricRow {
  rowKey?: string
  source: string
  totalQty: string
  totalBoxQty: string
  totalPalletQty: string
  totalVolume: string
  totalWeight: string
  chargeWeight: string
  length: string
  width: string
  height: string
  editable?: boolean
  editableValues?: Partial<Record<PackageMetricEditableField, number | undefined>>
}

export const MM3_PER_M3 = 1000000000
export const G_PER_KG = 1000

export const packageVolumeUnitOptions: PackageUnitOption<PackageVolumeUnit>[] = [
  { label: 'm³', value: 'm3' },
  { label: 'cm³', value: 'cm3' }
]

export const packageWeightUnitOptions: PackageUnitOption<PackageWeightUnit>[] = [
  { label: 'kg', value: 'kg' },
  { label: 'g', value: 'g' }
]

export const packageChargeWeightUnitOptions: PackageUnitOption<PackageChargeWeightUnit>[] = [
  { label: 'kgs', value: 'kgs' },
  { label: 'gs', value: 'gs' }
]

export const packageLengthUnitOptions: PackageUnitOption<PackageLengthUnit>[] = [
  { label: 'm', value: 'm' },
  { label: 'cm', value: 'cm' },
  { label: 'mm', value: 'mm' }
]

export function formatPackageQuantity(value?: number) {
  const numberValue = Number(value || 0)
  if (numberValue <= 0) return '-'
  return numberValue.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

export function formatPackageMeasure(value?: number) {
  const numberValue = Number(value || 0)
  if (numberValue <= 0) return '-'
  return numberValue.toLocaleString(undefined, { maximumFractionDigits: 6 })
}

export function convertMm3ToM3(value?: number) {
  return Number(value || 0) / MM3_PER_M3
}

export function convertM3ToMm3(value: number) {
  return Number((value * MM3_PER_M3).toFixed(2))
}

export function convertGToKg(value?: number) {
  return Number(value || 0) / G_PER_KG
}

export function convertKgToG(value: number) {
  return Number((value * G_PER_KG).toFixed(2))
}

export function convertPackageVolume(valueM3?: number, unit: PackageVolumeUnit = 'm3') {
  const value = Number(valueM3 || 0)
  if (unit === 'cm3') return value * 1000000
  return value
}

export function convertPackageVolumeToMm3(value?: number, unit: PackageVolumeUnit = 'm3') {
  const numberValue = Number(value || 0)
  if (unit === 'cm3') return Number((numberValue * 1000).toFixed(2))
  return convertM3ToMm3(numberValue)
}

export function convertPackageWeight(valueKg?: number, unit: PackageWeightUnit | PackageChargeWeightUnit = 'kg') {
  const value = Number(valueKg || 0)
  if (unit === 'g' || unit === 'gs') return value * G_PER_KG
  return value
}

export function convertPackageWeightToG(value?: number, unit: PackageWeightUnit | PackageChargeWeightUnit = 'kg') {
  const numberValue = Number(value || 0)
  if (unit === 'g' || unit === 'gs') return Number(numberValue.toFixed(2))
  return convertKgToG(numberValue)
}

export function convertPackageLength(valueMm?: number, unit: PackageLengthUnit = 'mm') {
  const value = Number(valueMm || 0)
  if (unit === 'm') return value / 1000
  if (unit === 'cm') return value / 10
  return value
}

export function convertPackageLengthToMm(value?: number, unit: PackageLengthUnit = 'mm') {
  const numberValue = Number(value || 0)
  if (unit === 'm') return Number((numberValue * 1000).toFixed(2))
  if (unit === 'cm') return Number((numberValue * 10).toFixed(2))
  return Number(numberValue.toFixed(2))
}
